// Sends one address-history sync and resolves with the backend's updated_count, or
// rejects with the backend's message.
export type PutAddressHistory = (
	appointmentId: string,
	applyToAllVisits: boolean,
) => Promise<number>;

export type VisitRef = { id: string; visitNumber: number };

export type AddressScopeResult =
	| { mode: "all"; updatedCount: number }
	| {
			mode: "subset";
			succeeded: VisitRef[];
			failed: (VisitRef & { message: string })[];
	  };

// Every visit selected keeps today's single apply_to_all_visits request, which the backend
// runs in one transaction — it updates all of them or none, so its failure simply rejects.
// A subset goes out one visit at a time with apply_to_all_visits false; those can land
// partially, so each outcome is kept against its visit number.
export async function applyAddressToVisits({
	appointmentId,
	allVisits,
	selectedIds,
	put,
}: {
	appointmentId: string;
	allVisits: VisitRef[];
	selectedIds: string[];
	put: PutAddressHistory;
}): Promise<AddressScopeResult> {
	const selected = allVisits.filter((v) => selectedIds.includes(v.id));

	if (selected.length === allVisits.length) {
		return { mode: "all", updatedCount: await put(appointmentId, true) };
	}

	const succeeded: VisitRef[] = [];
	const failed: (VisitRef & { message: string })[] = [];
	// Sequential on purpose: the visits share one series, and there are only a handful.
	for (const visit of selected) {
		try {
			await put(visit.id, false);
			succeeded.push(visit);
		} catch (err) {
			failed.push({
				...visit,
				message: err instanceof Error ? err.message : String(err),
			});
		}
	}

	return { mode: "subset", succeeded, failed };
}

export type ComparableAddress = {
	addressLine?: string | null;
	locationId?: number | null;
	postalCode?: string | null;
	latitude?: number | null;
	longitude?: number | null;
};

const toCoordinate = (value?: number | null) =>
	value == null ? null : value.toFixed(6);

// The one rule for "same address", used both to tell whether a series shares a single
// current address and to mark a visit already at the patient's active address. Address
// line and location match exactly, postal code too with a blank one equal to none (snapshots
// hold "" where a filled-in address sends null); coordinates match to 6 decimals.
export function isSameAddress(
	a?: ComparableAddress | null,
	b?: ComparableAddress | null,
): boolean {
	if (!a || !b) return false;

	return (
		a.addressLine === b.addressLine &&
		a.locationId === b.locationId &&
		(a.postalCode || null) === (b.postalCode || null) &&
		toCoordinate(a.latitude) === toCoordinate(b.latitude) &&
		toCoordinate(a.longitude) === toCoordinate(b.longitude)
	);
}

// True when every visit holds the same current address — or when none of them has one.
export function sharesOneAddress(
	addresses: (ComparableAddress | null | undefined)[],
): boolean {
	const [first] = addresses;
	return addresses.every((address) =>
		first ? isSameAddress(address, first) : !address,
	);
}

// Where the fill-in form starts: the opened visit, unless it is locked — the Orders page
// opens the dialog on the order's first visit — then the first visit still open to change.
export function pickPrefillVisit<T extends { id: string }>(
	visits: T[],
	openedId: string,
	isLocked: (visit: T) => boolean,
): T | undefined {
	const opened = visits.find((visit) => visit.id === openedId);
	return opened && !isLocked(opened)
		? opened
		: visits.find((visit) => !isLocked(visit));
}

// Chosen visits that already have a therapist and whose address would actually change.
// Only a warning: the dialog never blocks on it.
export function visitsToWarnAboutTherapist<
	T extends {
		id: string;
		therapistName?: string | null;
		visitAddress?: ComparableAddress | null;
	},
>(visits: T[], selectedIds: string[], target?: ComparableAddress | null): T[] {
	if (!target) return [];
	return visits.filter(
		(visit) =>
			selectedIds.includes(visit.id) &&
			!!visit.therapistName &&
			!isSameAddress(visit.visitAddress, target),
	);
}

// A visit address as Order Details carries it: the city in place of a location id.
export type VisitAddressSnapshot = {
	city?: string | null;
	addressLine?: string | null;
	postalCode?: string | null;
	latitude?: number | null;
	longitude?: number | null;
	notes?: string | null;
};

// Groups items that share one address, in first-seen order; items without an address form a
// group of their own. Blank postal code and notes equal none, as on the backend's comparison.
export function groupByAddress<T>(
	items: T[],
	addressOf: (item: T) => VisitAddressSnapshot | null | undefined,
): { address: VisitAddressSnapshot | null; items: T[] }[] {
	const groups = new Map<
		string,
		{ address: VisitAddressSnapshot | null; items: T[] }
	>();
	for (const item of items) {
		const address = addressOf(item) ?? null;
		const key = address
			? JSON.stringify([
					address.city ?? null,
					address.addressLine?.trim() ?? null,
					address.postalCode || null,
					toCoordinate(address.latitude),
					toCoordinate(address.longitude),
					address.notes || null,
				])
			: "none";
		const group = groups.get(key) ?? { address, items: [] };
		group.items.push(item);
		groups.set(key, group);
	}
	return [...groups.values()];
}

// "#1–#4, #6": consecutive visit numbers collapse into a range.
export function formatVisitNumbers(numbers: number[]): string {
	const sorted = [...numbers].sort((a, b) => a - b);
	const ranges: string[] = [];
	for (let i = 0; i < sorted.length; i++) {
		const start = sorted[i];
		while (sorted[i + 1] === sorted[i] + 1) i++;
		ranges.push(start === sorted[i] ? `#${start}` : `#${start}–#${sorted[i]}`);
	}
	return ranges.join(", ");
}

export type VisitNoteKind =
	| "failed"
	| "locked"
	| "therapist"
	| "already"
	| "therapistName";

// The one note a visit row in Update Address shows, highest-priority reason first, so a row
// never stacks every flag at once. A visit's own address is not a note: when the series'
// addresses differ, the row always shows it.
export function visitNoteKind(flags: {
	failed: boolean;
	locked: boolean;
	therapistAffected: boolean;
	alreadyTarget: boolean;
	hasTherapist: boolean;
}): VisitNoteKind | null {
	if (flags.failed) return "failed";
	if (flags.locked) return "locked";
	if (flags.therapistAffected) return "therapist";
	if (flags.alreadyTarget) return "already";
	if (flags.hasTherapist) return "therapistName";
	return null;
}
